import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { updateDeviceStatus } from '../common/device/initialize-devices';
import { DeviceType } from '../common/enumerations/device-type';
import XLRLogger from '../common/helpers/logger';
import { RdsResponseType, ScannerDeviceMethods } from './device-enums';
import { DeviceResponse, sendPeripheralMessage } from './rds-connection';

const loggerSource = 'devices/scanner-operations.ts';

const scanners: Map<
  string,
  { enabled: boolean; logicalName: string; dataEventEnabled: boolean }
> = new Map<string, { enabled: boolean; logicalName: string; dataEventEnabled: boolean }>();

export const initializeScanner = (
  logicalName: string,
  enabled: boolean,
  dataEventEnabled: boolean,
): void => {
  scanners.set(logicalName, { enabled, logicalName, dataEventEnabled });
};

const anyCanScan = (): boolean => {
  return [...scanners.values()].some(scanner => scanner.enabled);
};

const allCanScan = (): boolean => {
  const allTrue = (previous, { enabled }): boolean => {
    return previous && enabled;
  };
  return [...scanners.values()].reduce(allTrue, true);
};

/**
 * Check the status of the collection of initialized scanner devices.
 * Returns true if any (or all) scanners are enabled.
 * @param option defaults to 'ANY'
 */
export const canScannerScan = (option?: 'ANY' | 'ALL'): boolean => {
  switch (option) {
    case 'ALL':
      return allCanScan();
    case 'ANY':
    default:
      return anyCanScan();
  }
};

/** This delegate-like fn pointer is set by consumers of this module,
 * shared by all scanners!
 */
let _callback: ScannedDataHandler | undefined;
type CallbackStackItem = readonly [ScannedDataHandler | undefined, string | undefined];

/**
 * Scanner callbacks may be stacked by successive modal calls, and restored when
 * those modals are dismissed.
 */
const _callbackHistoryStack: CallbackStackItem[] = [];

/**
 * Provide a method to handle data messages received from any scanner.
 *
 * Calling this without providing a callback disables the scanners.
 *
 * @param callback optionally omit this to disable the scanner.
 */
export const updateScannerCallback = (callback?: ScannedDataHandler): void => {
  _callback = callback;
  if (callback) {
    enableScanner();
    return;
  }
  disableScanner();
};

/**
 * returns the current scanner callback.
 */
export const getScannerCallback = (): ScannedDataHandler | undefined => {
  return _callback;
};

export const onDataScanned = (data: string): Promise<void> => {
  if (_callback) {
    const span = createSpan('handle-scanned-data');
    return _callback(data)
      .catch(popupMessage)
      .finally(() => endSpan(span));
  }
  XLRLogger.debug({
    data: { data },
    message: 'Data event received, no undefined callback',
    source: 'devices/scanner-operations.ts',
  });
  return Promise.resolve();
};

/**
 * Tell RDS to enable scanner hardware.
 * @param logicalName optionally specify a single scanner. Omit to
 * enable all initialized scanners.
 */
export const enableScanner = (logicalName?: string): void => {
  if (logicalName) {
    sendScannerActionMessage(logicalName, ScannerDeviceMethods.ENABLE);
    sendScannerActionMessage(logicalName, ScannerDeviceMethods.ENABLE_DATA_EVENTS);
    return;
  }
  scanners.forEach(({ logicalName }) => enableScanner(logicalName));
};

/**
 * Tell RDS to disable scanner hardware.
 * @param logicalName optionally specify a single scanner. Omit to
 * enable all initialized scanners.
 */
export const disableScanner = (logicalName?: string): void => {
  if (logicalName) {
    sendScannerActionMessage(logicalName, ScannerDeviceMethods.DISABLE);
    return;
  }
  scanners.forEach(({ logicalName }) => disableScanner(logicalName));
};

/**
 * Tell RDS to enable dataEvents for scanner.
 * @param logicalName optionally specify a single scanner. Omit to
 * enable all initialized scanners.
 */
export const enableDataEvents = (logicalName?: string): void => {
  if (logicalName) {
    sendScannerActionMessage(logicalName, ScannerDeviceMethods.ENABLE_DATA_EVENTS);
    return;
  }
  scanners.forEach(({ logicalName }) => enableDataEvents(logicalName));
};

/**
 * Clear all scanner callbacks, disables scanners.
 */
export const resetScanner = (): void => {
  while (_callbackHistoryStack.length) {
    _callbackHistoryStack.pop();
  }
  updateScannerCallback();
};

/**
 * Enable scanning with a new scanner callback, by pushing current scanner state onto
 * a stack.
 * @param callback new scanner callback, optionally provide undefined to disable scanning.
 * @param id optional but encouraged.
 */
export const pushScannerState = (callback?: ScannedDataHandler, id?: string): void => {
  _callbackHistoryStack.push([_callback, id]);
  updateScannerCallback(callback);
};

/**
 * Restore previous scanner callback state.
 */
export const popScannerState = (id?: string): ScannedDataHandler | undefined => {
  if (_callbackHistoryStack.length) {
    const was = _callback;
    let index = _callbackHistoryStack.length - 1;

    if (id) {
      const indexForId = _callbackHistoryStack.findIndex(stackItem => stackItem[1] === id);
      if (indexForId < 0) {
        // throw Error(`Scanner state not found for ${id}`);
        XLRLogger.error({
          data: { id, _callbackStack: _callbackHistoryStack },
          message: `Scanner state not found for ${id}`,
          source: 'scanner-operations.ts',
        });
        return;
      }
      index = indexForId;
    }
    const [callback] = _callbackHistoryStack.splice(index, 1)[0];
    updateScannerCallback(callback);
    return was;
  }
  XLRLogger.error({
    data: { _callbackStack: _callbackHistoryStack },
    message: `Can't pop empty stack`,
    source: 'scanner-operations.ts',
  });
  return;
};

/**
 * Clear scanner callback history.
 */
export const resetScannerStates = (): void => {
  let original: CallbackStackItem | undefined;
  while (_callbackHistoryStack.length) {
    original = _callbackHistoryStack.pop();
  }
  if (original) {
    const [callback] = original;
    return updateScannerCallback(callback);
  }
  return updateScannerCallback();
};

export const sendScannerActionMessage = (
  logicalName,
  actionType: ScannerDeviceMethods,
): Promise<void> => {
  let url: string;
  switch (actionType) {
    case ScannerDeviceMethods.ENABLE:
      url = '/scanner/enable';
      break;
    case ScannerDeviceMethods.DISABLE:
      url = '/scanner/disable';
      break;
    case ScannerDeviceMethods.ENABLE_DATA_EVENTS:
      url = '/scanner/data-enable';
      break;
  }

  const payload = {
    type: DeviceType.Scanner,
    logicalName,
  };

  return sendPeripheralMessage(payload, url).then(
    value => {
      return handleScannerMessage(value);
    },
    reason => {
      return XLRLogger.debug({
        data: reason,
        message: `sendPeripheralMessage rejected`,
        source: loggerSource,
      });
    },
  );
};

export const handleScannerMessage = (scannerData: DeviceResponse): Promise<void> | void => {
  if (scannerData.responseType === RdsResponseType.DATA) {
    const { data, logicalName } = scannerData.metaDataMap;
    XLRLogger.debug({
      data: { scannerData },
      message: 'Received some data',
      source: loggerSource,
    });
    if (!data) {
      return;
    }
    return onDataScanned(data.trim()).then(() => enableDataEvents(logicalName));
  }

  const deviceName = scannerData?.metaDataMap?.logicalName;

  if (!deviceName) {
    XLRLogger.error({
      message: 'RDS Scanner Error: logicalName missing',
      source: 'devices/scanner-operations.ts',
      data: scannerData,
    });
    return;
  }

  if (scannerData.responseType === RdsResponseType.ERROR || scannerData.error) {
    XLRLogger.error({
      message: 'RDS Scanner Error',
      source: 'devices/scanner-operations.ts',
      data: scannerData,
    });
    return;
  }

  if (scannerData.responseType === RdsResponseType.HEALTH_CHECK_SUCCESS) {
    updateDeviceStatus({
      deviceName,
      initialized: true,
      deviceType: DeviceType.Scanner,
    });
    XLRLogger.debug({
      message: `Scanner Status: ${scannerData.message}`,
      source: 'devices/scanner-operations.ts',
      data: scannerData,
    });
    return;
  }
  if (scannerData.responseType === RdsResponseType.HEALTH_CHECK_FAILURE) {
    updateDeviceStatus({
      deviceName,
      initialized: false,
      deviceType: DeviceType.Scanner,
    });
    XLRLogger.debug({
      message: `Scanner Status: ${scannerData.message}`,
      source: 'devices/scanner-operations.ts',
      data: scannerData,
    });
    return;
  }
  if (scannerData.responseType === RdsResponseType.SET_DEVICE_ENABLED) {
    const { logicalName, deviceEnabled } = scannerData.metaDataMap;
    if (logicalName && scanners.has(logicalName)) {
      const scanner = scanners.get(logicalName);
      if (scanner) {
        scanners.set(logicalName, {
          ...scanner,
          enabled: deviceEnabled === 'true',
        });
      }
    }
    const message =
      scannerData.message || `${deviceName}: ${scannerData.responseType} ${scannerData.success}`;
    XLRLogger.debug({
      message: `Scanner Status: ${message}`,
      source: 'devices/scanner-operations.ts',
      data: scannerData,
    });
    return;
  }
  if (scannerData.responseType === RdsResponseType.SET_DATA_EVENT_ENABLED) {
    const { logicalName, dataEventEnabled } = scannerData.metaDataMap;
    if (logicalName && scanners.has(logicalName)) {
      const scanner = scanners.get(logicalName);
      if (scanner) {
        scanners.set(logicalName, {
          ...scanner,
          dataEventEnabled: dataEventEnabled === 'true',
        });
      }
    }
    const message =
      scannerData.message || `${deviceName}: ${scannerData.responseType} ${scannerData.success}`;
    XLRLogger.debug({
      message: `Scanner Status: ${message}`,
      source: 'devices/scanner-operations.ts',
      data: scannerData,
    });
    return;
  }
  if (scannerData.responseType === RdsResponseType.STATUS_UPDATE) {
    XLRLogger.debug({
      message: `Scanner Status: ${scannerData.message}`,
      source: 'devices/scanner-operations.ts',
      data: scannerData,
    });
    return;
  }

  XLRLogger.info({
    message: 'Unrecognized message from Scanner',
    source: 'devices/scanner-operations.ts',
    data: scannerData,
  });
  return;
};

export type ScannedDataHandler = (data: string) => Promise<void>;
